import {
  ADD_PRINT_STORE_SHOPPING_CART_ITEM_WITHOUT_TEMPLATE,
  ADD_PRINT_STORE_SHOPPING_CART_ITEM_WITH_DH_TEMPLATE,
  ADD_PRINT_STORE_SHOPPING_CART_ITEM_WITH_TEMPLATE,
  CONFIRM_AND_DELETE_PRINT_STORE_SHOPPING_CART_ORDER,
  CREATE_PRINT_STORE_CHECKOUT,
  DELETE_PRINT_STORE_SHOPPING_CART_ITEM,
  EDIT_PRINT_STORE_SHOPPING_CART_ITEM_QTY
} from '@dtx-company/flow-codegen/src/code/mutations'
import {
  AddShoppingCartItemWithDhTemplateMutation,
  AddShoppingCartItemWithDhTemplateMutationVariables,
  AddShoppingCartItemWithTemplateMutation,
  AddShoppingCartItemWithTemplateMutationVariables,
  AddShoppingCartItemWithoutTemplateMutation,
  AddShoppingCartItemWithoutTemplateMutationVariables,
  ConfirmAndDeleteShoppingCartOrderMutation,
  ConfirmAndDeleteShoppingCartOrderMutationVariables,
  CreatePrintStoreCheckoutMutation,
  CreatePrintStoreCheckoutMutationVariables,
  DeleteShoppingCartItemMutation,
  DeleteShoppingCartItemMutationVariables,
  EditShoppingCartItemQtyMutation,
  EditShoppingCartItemQtyMutationVariables
} from '@dtx-company/flow-codegen/src/code/generated.types'
import { flowcodeApi } from './init'
import {
  getPatchResultAddItem,
  getPatchResultConfirmAndDeleteItem,
  getPatchResultDeleteItem,
  getPatchResultEditItem,
  undoPatch
} from './printStoreQueries.util.updateQueryData'
import { sendSuccessNotification } from '../../../utils/notifications'

export const printStoreMutations = flowcodeApi.injectEndpoints({
  endpoints: builder => ({
    addItemWithoutTemplate: builder.mutation<
      AddShoppingCartItemWithoutTemplateMutation,
      AddShoppingCartItemWithoutTemplateMutationVariables
    >({
      query: ({ productId, priceId, quantity, batchId }) => ({
        document: ADD_PRINT_STORE_SHOPPING_CART_ITEM_WITHOUT_TEMPLATE,
        variables: { productId, priceId, quantity, batchId },
        authOnly: true
      }),
      invalidatesTags: ['ShoppingCart'],
      async onQueryStarted(item, { dispatch, queryFulfilled }) {
        const patchResult = getPatchResultAddItem(item, dispatch)
        try {
          await queryFulfilled
          sendSuccessNotification('Item added to cart')
        } catch {
          undoPatch(patchResult, 'Error adding item to cart')
        }
      }
    }),

    addItemWithTemplate: builder.mutation<
      AddShoppingCartItemWithTemplateMutation,
      AddShoppingCartItemWithTemplateMutationVariables
    >({
      query: ({ productId, priceId, quantity, batchId, templateId, templateDetails }) => ({
        document: ADD_PRINT_STORE_SHOPPING_CART_ITEM_WITH_TEMPLATE,
        variables: { productId, priceId, quantity, batchId, templateId, templateDetails },
        authOnly: true
      }),
      invalidatesTags: ['ShoppingCart'],
      async onQueryStarted(item, { dispatch, queryFulfilled }) {
        const patchResult = getPatchResultAddItem(item, dispatch)
        try {
          await queryFulfilled
          sendSuccessNotification('Item added to cart')
        } catch {
          undoPatch(patchResult, 'Error adding item to cart')
        }
      }
    }),

    addItemWithDhTemplate: builder.mutation<
      AddShoppingCartItemWithDhTemplateMutation,
      AddShoppingCartItemWithDhTemplateMutationVariables
    >({
      query: variables => ({
        document: ADD_PRINT_STORE_SHOPPING_CART_ITEM_WITH_DH_TEMPLATE,
        variables,
        authOnly: true
      }),
      invalidatesTags: ['ShoppingCart'],
      async onQueryStarted(item, { dispatch, queryFulfilled }) {
        const patchResult = getPatchResultAddItem(item, dispatch)
        try {
          await queryFulfilled
          sendSuccessNotification('Item added to cart')
        } catch {
          undoPatch(patchResult, 'Error adding item to cart')
        }
      }
    }),

    editItemQuantity: builder.mutation<
      EditShoppingCartItemQtyMutation,
      EditShoppingCartItemQtyMutationVariables
    >({
      query: ({ itemId, newQuantity }) => ({
        document: EDIT_PRINT_STORE_SHOPPING_CART_ITEM_QTY,
        variables: { itemId, newQuantity },
        authOnly: true
      }),
      invalidatesTags: ['ShoppingCart'],
      async onQueryStarted({ itemId, newQuantity }, { dispatch, queryFulfilled }) {
        const patchResult = getPatchResultEditItem(itemId, newQuantity, dispatch)
        try {
          await queryFulfilled
          sendSuccessNotification('Item quantity updated')
        } catch {
          undoPatch(patchResult, 'Error updating item quantity')
        }
      }
    }),

    deleteItem: builder.mutation<
      DeleteShoppingCartItemMutation,
      DeleteShoppingCartItemMutationVariables
    >({
      query: ({ itemId }) => ({
        document: DELETE_PRINT_STORE_SHOPPING_CART_ITEM,
        variables: { itemId },
        authOnly: true
      }),
      invalidatesTags: ['ShoppingCart'],
      async onQueryStarted({ itemId }, { dispatch, queryFulfilled }) {
        const patchResult = getPatchResultDeleteItem(itemId, dispatch)
        try {
          await queryFulfilled
          sendSuccessNotification('Item deleted from cart')
        } catch {
          undoPatch(patchResult, 'Error deleting item from cart')
        }
      }
    }),

    checkout: builder.mutation<
      CreatePrintStoreCheckoutMutation,
      CreatePrintStoreCheckoutMutationVariables
    >({
      query: variables => ({
        document: CREATE_PRINT_STORE_CHECKOUT,
        variables,
        authOnly: true
      })
    }),

    confirmOrder: builder.mutation<
      ConfirmAndDeleteShoppingCartOrderMutation,
      ConfirmAndDeleteShoppingCartOrderMutationVariables
    >({
      query: ({ cartId }) => ({
        document: CONFIRM_AND_DELETE_PRINT_STORE_SHOPPING_CART_ORDER,
        variables: { cartId },
        authOnly: true
      }),
      invalidatesTags: ['ShoppingCart'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const patchResult = getPatchResultConfirmAndDeleteItem(dispatch)
        try {
          await queryFulfilled
        } catch {
          undoPatch(patchResult, 'Error clearing cart')
        }
      }
    })
  })
})

export const {
  useAddItemWithDhTemplateMutation,
  useAddItemWithoutTemplateMutation,
  useAddItemWithTemplateMutation,
  useCheckoutMutation,
  useConfirmOrderMutation,
  useDeleteItemMutation,
  useEditItemQuantityMutation,
  usePrefetch
} = printStoreMutations
